// Running text marquee.
// The first [data-marquee-item] holds the merchant-editable content. It is cloned (aria-hidden,
// ids and editor attributes removed) until one group spans the viewport, then the whole group is
// duplicated so a CSS translate of -50% loops seamlessly. Animation lives in CSS and is skipped
// for reduced motion.
class Marquee extends HTMLElement {
  #controller = null;
  #observer = null;

  connectedCallback() {
    this.#controller = new AbortController();
    this.track = this.querySelector('[data-marquee-track]');
    this.item = this.track?.querySelector('[data-marquee-item]');
    if (!this.track || !this.item) return;
    this.#build();
    window.addEventListener('resize', () => this.#build(), { signal: this.#controller.signal });
    // Rebuild the copies whenever the original content changes (e.g. text edited in the editor).
    let frame = 0;
    this.#observer = new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => this.#build());
    });
    this.#observer.observe(this.item, { childList: true, subtree: true, characterData: true });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
  }

  #clone(node) {
    const copy = node.cloneNode(true);
    copy.setAttribute('aria-hidden', 'true');
    copy.removeAttribute('data-marquee-item');
    for (const el of [copy, ...copy.querySelectorAll('*')]) {
      el.removeAttribute('id');
      for (const attr of Array.from(el.attributes)) {
        if (attr.name.startsWith('data-shopify')) el.removeAttribute(attr.name);
      }
    }
    copy.setAttribute('data-marquee-clone', '');
    return copy;
  }

  #build() {
    this.track.querySelectorAll('[data-marquee-clone]').forEach((el) => el.remove());
    const itemWidth = this.item.getBoundingClientRect().width;
    if (itemWidth === 0) return;
    const perGroup = Math.max(1, Math.ceil(this.getBoundingClientRect().width / itemWidth));
    // First group: original + clones; second group: identical copies for the seamless loop.
    const total = perGroup * 2;
    for (let i = 1; i < total; i += 1) this.track.append(this.#clone(this.item));
    const duration = Math.max(10, total * 4);
    this.track.style.setProperty('--marquee-duration', `${duration}s`);
  }
}

if (!customElements.get('s-marquee')) customElements.define('s-marquee', Marquee);
